import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api } from '../services/api'
import { useAuthStore } from '../store/auth.store'
import { UserRole } from '@teffy/shared-types'
import { Button, Field, Input } from '../components/ui'

type View = 'login' | 'forgot' | 'reset'

export default function LoginPage() {
  const [view, setView] = useState<View>('login')
  const [resetEmail, setResetEmail] = useState('')

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 w-full max-w-sm p-8 space-y-6">
        <div className="text-center">
          <h1 className="text-4xl font-black tracking-widest text-gray-900">TEFFY</h1>
          <p className="text-sm text-gray-500 mt-1">Panel de administración</p>
        </div>

        {view === 'login' && (
          <LoginForm onForgot={() => setView('forgot')} />
        )}
        {view === 'forgot' && (
          <ForgotForm
            onCodeSent={(email) => { setResetEmail(email); setView('reset') }}
            onBack={() => setView('login')}
          />
        )}
        {view === 'reset' && (
          <ResetForm
            email={resetEmail}
            onDone={() => setView('login')}
            onBack={() => setView('forgot')}
          />
        )}
      </div>
    </div>
  )
}

// ─── Login ────────────────────────────────────────────────────────────────────
function LoginForm({ onForgot }: { onForgot: () => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const setAuth = useAuthStore((s) => s.setAuth)
  const navigate = useNavigate()

  const { mutate: login, isPending } = useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/auth/login', { email, password })
      return data
    },
    onSuccess: (data) => {
      if (![UserRole.ADMIN, UserRole.OWNER, UserRole.SUPERADMIN, UserRole.CORPORATE].includes(data.user.role)) {
        setError('Esta pantalla es solo para administradores')
        return
      }
      setAuth({
        token: data.token,
        role: data.user.role,
        userId: data.user.id,
        restaurantId: data.restaurant?.id ?? null,
        restaurantName: data.restaurant?.name ?? null,
        userName: data.user.name,
        groupId: data.group?.id ?? null,
        groupName: data.group?.name ?? null,
      })
      if (data.user.role === UserRole.SUPERADMIN) navigate('/superadmin')
      else if (data.user.role === UserRole.CORPORATE) navigate('/corporate')
      else navigate('/dashboard')
    },
    onError: () => setError('Email o contraseña incorrectos'),
  })

  return (
    <div className="space-y-4">
      <Field label="Email">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@mirestaurante.com"
          autoComplete="email"
        />
      </Field>
      <Field label="Contraseña">
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          onKeyDown={(e) => e.key === 'Enter' && login()}
        />
      </Field>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <Button
        className="w-full justify-center"
        loading={isPending}
        disabled={!email || !password}
        onClick={() => login()}
      >
        Entrar
      </Button>

      <button
        type="button"
        onClick={onForgot}
        className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors"
      >
        ¿Olvidaste tu contraseña?
      </button>
    </div>
  )
}

// ─── Paso 1: solicitar código ─────────────────────────────────────────────────
function ForgotForm({
  onCodeSent,
  onBack,
}: {
  onCodeSent: (email: string) => void
  onBack: () => void
}) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/auth/forgot-password', { email })
      return data
    },
    onSuccess: () => {
      onCodeSent(email)
    },
    onError: () => setError('Ocurrió un error, intenta de nuevo'),
  })

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-bold text-gray-900">Recuperar contraseña</h2>
        <p className="text-xs text-gray-500 mt-1">
          Ingresa tu email y te enviaremos un código de 6 dígitos. Revisa tu bandeja de entrada y spam.
        </p>
      </div>

      <Field label="Email">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@mirestaurante.com"
          autoComplete="email"
        />
      </Field>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <Button
        className="w-full justify-center"
        loading={isPending}
        disabled={!email}
        onClick={() => mutate()}
      >
        Enviar código
      </Button>

      <button
        type="button"
        onClick={onBack}
        className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors"
      >
        ← Volver al inicio de sesión
      </button>
    </div>
  )
}

// ─── Paso 2: ingresar código + nueva contraseña ───────────────────────────────
function ResetForm({
  email,
  onDone,
  onBack,
}: {
  email: string
  onDone: () => void
  onBack: () => void
}) {
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      if (newPassword !== confirm) throw new Error('Las contraseñas no coinciden')
      if (newPassword.length < 8) throw new Error('Mínimo 8 caracteres')
      const { data } = await api.post('/auth/reset-password', { email, code, newPassword })
      return data
    },
    onSuccess: () => {
      setError('')
      alert('Contraseña actualizada. Inicia sesión con tu nueva contraseña.')
      onDone()
    },
    onError: (e: Error) => setError(e.message || 'Código inválido o expirado'),
  })

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-bold text-gray-900">Nueva contraseña</h2>
        <p className="text-xs text-gray-500 mt-1">
          Ingresa el código que recibiste y tu nueva contraseña.
        </p>
      </div>

      <Field label="Código de 6 dígitos">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456"
          maxLength={6}
          className="tracking-widest text-center text-lg font-bold"
        />
      </Field>

      <Field label="Nueva contraseña">
        <Input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Mínimo 8 caracteres"
        />
      </Field>

      <Field label="Confirmar contraseña">
        <Input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="••••••••"
        />
      </Field>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <Button
        className="w-full justify-center"
        loading={isPending}
        disabled={code.length !== 6 || !newPassword || !confirm}
        onClick={() => mutate()}
      >
        Cambiar contraseña
      </Button>

      <button
        type="button"
        onClick={onBack}
        className="w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors"
      >
        ← Solicitar nuevo código
      </button>
    </div>
  )
}
